import { HOUSE_SECTIONS } from '../constants/houseSections';

/**
 * Normalize iteration/submission media into a structure keyed by section for easy display.
 * Supports:
 * - Array of items with optional .section: [{ section, key, content_type }, ...]
 * - Object keyed by section (already grouped): { "Hall": [...], "Kitchen": [...] }
 * - Legacy flat array (no section): items go under "Other"
 *
 * @param {string|Array|Object} media - Raw media from API (string JSON, array, or object)
 * @returns {Object} { sectionName: [ mediaItem, ... ], ... } with section names as keys
 */
export function normalizeMediaBySection(media) {
  let items = [];

  if (media == null) {
    return {};
  }

  if (typeof media === 'string') {
    try {
      const parsed = JSON.parse(media);
      if (Array.isArray(parsed)) {
        items = parsed;
      } else if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        // Already keyed by section
        const bySection = {};
        for (const [section, list] of Object.entries(parsed)) {
          const arr = Array.isArray(list) ? list : [list];
          if (section && arr.length) bySection[section] = arr;
        }
        return bySection;
      } else {
        items = [parsed];
      }
    } catch {
      items = media.split(',').filter((m) => m && m.trim()).map((m) => ({ key: m.trim() }));
    }
  } else if (Array.isArray(media)) {
    items = media;
  } else if (typeof media === 'object' && media !== null) {
    const keys = Object.keys(media);
    if (keys.length > 0) {
      const firstVal = media[keys[0]];
      if (Array.isArray(firstVal)) {
        return media;
      }
    }
    items = [media];
  }

  const bySection = {};
  for (const item of items) {
    const section = item?.section || item?.sectionName || 'Other';
    if (!bySection[section]) bySection[section] = [];
    bySection[section].push(item);
  }

  return bySection;
}

/**
 * Ordered list of section names for display (matches HOUSE_SECTIONS so "Other" last).
 */
export function getSectionDisplayOrder() {
  return [...HOUSE_SECTIONS];
}
